import { Paper, Tooltip } from '@mui/material';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValueLoadable } from 'recoil';

import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { useFeatureFlag } from 'src/app/hooks/useFeatureFlag';
import { inflightCadencesPage } from '../../recoil/atoms/inflightCadencesPage';
import { inflightCadencesSearch } from '../../recoil/atoms/inflightCadencesSearch';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import { DataTable } from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';

// import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import { DateTime } from 'luxon';
import { reformatCamelCaseString } from '../../common/helperFunctions/formatter';
import { Role } from '../../recoil/atoms/user';
import { cadenceActionProcess } from '../../recoil/selectors/cadenceActionProcess';
import { methodNameEnum } from '../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { CadenceModal } from '../components/modals/CadenceModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';

export function InflightCadences() {
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const { role } = permissionsLoadable.contents;
  const isSuperAdminBoolean = role === Role.SUPERADMIN;
  const { treatment } = useFeatureFlag('DASH-170-datatable-search', 'on');
  // state for serverside pagination
  const [page, setPage] = useRecoilState(inflightCadencesPage);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useRecoilState(inflightCadencesSearch);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [sortModel, setGridSortModel] = useState([
    {
      field: 'scheduledDate',
      sort: 'desc',
    },
  ]);
  const [
    businessDataCached,
    ,
    setRefetchToggleForCadence,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_DATA,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_DATA,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.[0] ?? cached.current
  );
  const handlePageChange = (page) => {
    setPage(page);
  };
  const handlePageSizeChange = (pageSize) => {
    setPageSize(pageSize);
  };
  // const handleGridSortModelChange = (params) => {
  //   console.log(params, 'sort model change ');
  // };
  const handleGridSortModelChange = (newSortModel) => {
    if (
      newSortModel?.[0] &&
      (newSortModel[0].field !== sortModel[0].field ||
        newSortModel[0].sort !== sortModel[0].sort)
    ) {
      setGridSortModel(newSortModel);
    }
  };
  const inflight = true;
  const cadenceActionProcesses = useRecoilValueLoadable(
    cadenceActionProcess({
      page,
      pageSize,
      search,
      inflight,
      sortModel,
      featureFlagTreatment: treatment,
    })
  );

  const [rowForEdit, handleRowForEdit] = useState({});
  const [cadenceEditModal, handleCadenceEditModal] = useState(false);

  const handleEdit = (row) => {
    handleCadenceEditModal(true);
    handleRowForEdit(row);
  };

  const nameFormatter = (params) => {
    const name = params.row.cadenceProcess.cadence.cadenceName;
    return (
      <Tooltip title="Edit">
        <Link onClick={() => handleEdit(params.row.cadenceProcess)} to="#">
          {name}
        </Link>
      </Tooltip>
    );
  };
  const statusFormatter = (params) => {
    return reformatCamelCaseString(params.value);
  };
  // const subStatusFormatter = (params) => {
  //   return reformatCamelCaseString(params.value);
  // };

  const total = cadenceActionProcesses.contents?.data?.count ?? 0;
  const rowsCached = useRef([]);
  // console.log(cadenceActionProcesses);
  /**
   *
   * NOTE: must use useMemo so rows do not get updated on re-render
   */
  rowsCached.current = useMemo(
    () =>
      cadenceActionProcesses.state === 'hasValue'
        ? cadenceActionProcesses.contents?.data?.entities?.map((ca) => ({
            ...ca,
            // id: ca.cadenceActionId,
            id: uuidv4(),
          }))
        : [],
    [cadenceActionProcesses]
  );
  const columns = [
    {
      field: 'cadenceActionProcessId',
      headerName: 'Process ID',
      headerClassName: dataTableScss.fullWidth + ' superStyle',
      cellClassName: dataTableScss.fullWidth,
      hide: !isSuperAdminBoolean,
    },
    {
      // to do add date selector
      field: 'createdDate',
      headerName: 'Created Date',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        DateTime.fromISO(params.value, {
          zone: businessDataCached.current?.timezone_php,
        }).toLocaleString(DateTime.DATETIME_SHORT),
    },
    {
      field: 'cadenceProcess.cadenceId',
      headerName: 'Cadence ID',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.row.cadenceProcess.cadenceId,
    },
    {
      field: 'cadenceProcess.cadenceVersion',
      headerName: 'Cadence Version',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.row.cadenceProcess.cadenceVersion,
    },
    {
      field: 'cadenceProcess.cadence.cadenceName',
      headerName: 'Cadence Name',
      valueFormatter: (params) => params.row.cadenceProcess.cadence.cadenceName,
      renderCell: nameFormatter,
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      /*valueGetter: (params) => params.row.cadenceProcess.cadence.cadenceName,
      renderCell: (params) => (
        <Tooltip title="Edit">
          <Link
            to={`/manage/cadences/${params.row.cadenceProcess.cadenceId}/edit`}
          >
            {params.value}
          </Link>
        </Tooltip>
      ),*/
    },
    {
      field: 'cadenceProcess.targetPhoneNumber',
      headerName: 'Target Phone Number',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) => params.row.cadenceProcess.targetPhoneNumber,
    },
    {
      field: 'type',
      headerName: 'Action Type',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'scheduledDate',
      headerName: 'Scheduled Date',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        DateTime.fromISO(params.value, {
          zone: businessDataCached.current?.timezone_php,
        }).toLocaleString(DateTime.DATETIME_SHORT),
    },
    {
      field: 'status',
      headerName: 'Status',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: statusFormatter,
    },
    {
      field: 'retryCount',
      headerName: 'Retry Count',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'maxRetries',
      headerName: 'Max Retry / Duration',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      valueFormatter: (params) =>
        `${params.value} / ${params.row.callRetryInMin} min`,
    },
  ];
  const handleSearchChange = useCallback(
    (value) => {
      // reset page back to 1 when feature flag is ON
      if (treatment === 'on') {
        setPage(1);
      }
      setSearch(value);
    },
    [setSearch, treatment]
  );
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Inflight-Cadence"
        search={search}
        // Feature flag: ON uses correct prop name, OFF uses old broken prop name
        {...(treatment === 'on'
          ? { setSearch: handleSearchChange }
          : { handleSearchChange: handleSearchChange }
        )}
        debounceTimeout={1000} // add 1/2 second debounce vs 100ms default value
        isLoading={cadenceActionProcesses.state === 'loading'} // do not block on search
      />
    ),
    [search, cadenceActionProcesses, treatment, handleSearchChange]
  );
  return (
    <Paper square={true} elevation={0}>
      <DataTable
        rows={rowsCached?.current ?? []}
        columns={columns}
        // TO DO: return total from server
        rowCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleGridSortModelChange}
        loading={cadenceActionProcesses.state === 'loading'}
        ToolbarComponent={ToolbarComponent}
        pagination
      />
      <CadenceModal
        show={!!cadenceEditModal}
        id={rowForEdit.cadenceId}
        enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
        handleClose={() => {
          setRefetchToggleForCadence({});
          handleCadenceEditModal(false);
        }}
      />
    </Paper>
  );
}
